import { useEffect, useCallback } from 'react'
import { useNotificationStore } from '@/store/useNotificationStore'
import { useAuthStore } from '@/store/useAuthStore'
import { useTaskStore } from '@/store/useTaskStore'
import { useSupplyStore } from '@/store/useSupplyStore'
import {
  requestNotificationPermission,
  scheduleTaskReminder,
  showBrowserNotification,
  showLowStockNotification,
} from '@/lib/notifications'
import { isToday, isBefore, parseISO } from 'date-fns'

export function useNotifications() {
  const currentUserId = useAuthStore((state) => state.currentUserId)
  const { preferences, fetchPreferences, initializePreferences, createNotification } = useNotificationStore()
  const tasks = useTaskStore((state) => state.tasks)
  const supplies = useSupplyStore((state) => state.supplies)

  // Initialize preferences and request permission
  useEffect(() => {
    if (currentUserId) {
      fetchPreferences(currentUserId).then(() => {
        if (!preferences) {
          initializePreferences(currentUserId)
        }
      })

      // Request notification permission if enabled
      if (preferences?.browser_notifications) {
        requestNotificationPermission()
      }
    }
  }, [currentUserId, preferences?.browser_notifications, fetchPreferences, initializePreferences])

  // Check for overdue tasks
  useEffect(() => {
    if (!currentUserId || !preferences?.enabled) return

    const checkOverdueTasks = () => {
      const now = new Date()

      tasks
        .filter((task) => task.assigned_to === currentUserId)
        .forEach((task) => {
          const dueDate = parseISO(task.start_date)

          if (isBefore(dueDate, now) && !isToday(dueDate)) {
            // Create in-app notification for overdue task
            createNotification(
              currentUserId,
              'task_overdue',
              'Overdue Task',
              `"${task.title}" is overdue`,
              { task_id: task.id }
            )
          }
        })
    }

    // Check immediately and then every hour
    checkOverdueTasks()
    const interval = setInterval(checkOverdueTasks, 60 * 60 * 1000)

    return () => clearInterval(interval)
  }, [currentUserId, tasks, preferences?.enabled, createNotification])

  // Check for low stock supplies
  useEffect(() => {
    if (!currentUserId || !preferences?.enabled || !preferences?.low_stock) return

    const checkLowStock = () => {
      supplies.forEach((supply) => {
        if (supply.quantity <= supply.min_quantity) {
          // Create in-app notification
          createNotification(
            currentUserId,
            'low_stock',
            'Low Stock Alert',
            `${supply.name} is running low (${supply.quantity} remaining)`,
            { supply_id: supply.id }
          )

          // Show browser notification if enabled
          if (preferences.browser_notifications) {
            showLowStockNotification(supply.name, supply.quantity)
          }
        }
      })
    }

    // Check immediately and then every 6 hours
    checkLowStock()
    const interval = setInterval(checkLowStock, 6 * 60 * 60 * 1000)

    return () => clearInterval(interval)
  }, [currentUserId, supplies, preferences?.enabled, preferences?.low_stock, preferences?.browser_notifications, createNotification])

  const scheduleReminder = useCallback(
    (taskTitle: string, dueDate: Date) => {
      if (!preferences?.enabled || !preferences?.browser_notifications) return null

      return scheduleTaskReminder(
        taskTitle,
        dueDate,
        preferences.reminder_time,
        () => {
          window.focus()
        }
      )
    },
    [preferences]
  )

  const sendNotification = useCallback(
    (
      type: 'task_reminder' | 'task_overdue' | 'achievement' | 'low_stock' | 'daily_summary',
      title: string,
      message: string,
      data?: Record<string, any>
    ) => {
      if (!currentUserId || !preferences?.enabled) return

      createNotification(currentUserId, type, title, message, data)

      // Show browser notification if enabled
      if (preferences.browser_notifications) {
        showBrowserNotification(title, {
          body: message,
          tag: type,
        })
      }
    },
    [currentUserId, preferences, createNotification]
  )

  return {
    scheduleReminder,
    sendNotification,
    preferences,
  }
}
